import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions | Blanco Pro Services" },
      { name: "description", content: "Terms and conditions, SMS messaging compliance, and website usage terms for Blanco Pro Services LLC." },
      { property: "og:title", content: "Terms & Conditions | Blanco Pro Services" },
      { property: "og:description", content: "Terms and conditions, SMS messaging compliance, and website usage terms for Blanco Pro Services LLC." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/terms" }],
  }),
  component: TermsPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 text-foreground">{title}</h2>
      <div className="space-y-4 text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="font-display font-bold text-lg mt-6 text-foreground">{children}</h3>;
}

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <header className="mb-14 border-b border-foreground/10 pb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3 font-display">Legal</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-4">Terms & Conditions</h1>
          <p className="text-foreground/60 text-sm">Blanco Pro Services LLC</p>
          <p className="text-foreground/60 text-sm">Effective Date: January 1, 2026</p>
        </header>

        <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">SMS Messaging Terms & Compliance</h2>

        <Section title="1. Program Description">
          <p>
            This messaging program sends appointment confirmations, appointment reminders, service updates, and customer support messages to
            customers who have booked an appointment with Blanco Pro Services LLC through our website at{" "}
            <a className="text-gold hover:underline" href="https://www.blancopros.com">https://www.blancopros.com</a>, through our scheduling forms, or
            by another approved method, and who have explicitly opted in to receive SMS notifications.
          </p>
          <p>
            SMS consent is collected through web forms using a dedicated checkbox for SMS authorization. Messages may include scheduling
            confirmations, appointment reminders, rescheduling updates, customer support communications, and other service-related notifications.
          </p>
        </Section>

        <Section title="2. Cancellation Instructions">
          <p>You may cancel the SMS service at any time by replying <strong>STOP</strong> to any message you receive from us.</p>
          <p>
            After sending STOP, you will receive a confirmation message verifying that you have been unsubscribed. Once confirmed, you will no longer
            receive SMS messages from Blanco Pro Services LLC unless you opt in again.
          </p>
          <p>To resume receiving messages, simply sign up again using one of our approved opt-in methods.</p>
        </Section>

        <Section title="3. Support Information">
          <p>If you experience issues with our messaging program, reply <strong>HELP</strong> for assistance or contact us during normal business hours.</p>
          <p>
            <strong>Email:</strong>{" "}
            <a className="text-gold hover:underline" href="mailto:sevice@blancopros.com">sevice@blancopros.com</a>
            <br />
            <strong>Phone:</strong>{" "}
            <a className="text-gold hover:underline" href="tel:+19088956133">+1 (908) 895-6133</a>
          </p>
        </Section>

        <Section title="4. Carrier Liability">
          <p>Wireless carriers are not responsible for delayed or undelivered messages.</p>
        </Section>

        <Section title="5. Message & Data Rates">
          <p>Standard message and data rates may apply for messages sent to you from us and from you to us.</p>
          <p>Message frequency varies depending on your appointments, requested services, and interactions with Blanco Pro Services LLC.</p>
          <p>For questions regarding your text messaging or data plan, please contact your wireless provider.</p>
        </Section>

        <Section title="6. Supported Carriers">
          <p>
            Our SMS program is supported by all major U.S. wireless carriers, including AT&T, Verizon, T-Mobile, Sprint, and most regional carriers.
          </p>
        </Section>

        <Section title="7. Age Restriction">
          <p>You must be at least 18 years of age to participate in our SMS messaging program.</p>
        </Section>

        <Section title="8. Privacy Policy">
          <p>
            For information regarding how we collect, use, and protect your personal information, please review our{" "}
            <Link className="text-gold hover:underline" to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            We comply with all applicable federal and state laws governing SMS communications, including the Telephone Consumer Protection Act (TCPA)
            and CTIA Messaging Principles and Best Practices.
          </p>
        </Section>

        <div className="border-t border-foreground/10 pt-10 mt-4">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">General Terms</h2>
        </div>

        <Section title="Website Use">
          <p>This website (the "Site") is owned and operated by Blanco Pro Services LLC ("Company," "we," "our," or "us").</p>
          <p>
            By accessing or using the Site, you agree to be bound by these Terms & Conditions, our Privacy Policy, and any additional terms that may
            apply to specific products or services offered by Blanco Pro Services LLC.
          </p>
          <p>Accessing the Site by any means, whether automated or otherwise, constitutes your acceptance of these Terms & Conditions.</p>
          <p>
            We reserve the right to revise these Terms & Conditions at any time. Updated versions will be posted on this website with a revised
            effective date. Continued use of the Site after changes have been posted constitutes your acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="Intellectual Property Rights">
          <SubHeading>Our Limited License to You</SubHeading>
          <p>
            This Site and all content available on it, including text, graphics, logos, images, documents, software, and other materials, are the
            property of Blanco Pro Services LLC or its licensors and are protected by applicable copyright, trademark, and intellectual property laws.
          </p>
          <p>The Site is provided solely for your personal, non-commercial use.</p>
          <p>
            Except as expressly permitted, you may not modify, reproduce, republish, upload, post, transmit, distribute, sell, create derivative works
            from, or otherwise exploit any material found on this Site without prior written permission.
          </p>
          <p>
            You may download or print one copy of individual pages for your personal, non-commercial use, provided that all copyright and proprietary
            notices remain intact.
          </p>

          <SubHeading>Your License to Us</SubHeading>
          <p>
            By submitting content to us through our website, contact forms, social media pages, reviews, photographs, videos, comments, or other
            digital platforms, you represent that you own the content or have permission to share it.
          </p>
          <p>
            You grant Blanco Pro Services LLC a perpetual, irrevocable, worldwide, royalty-free, non-exclusive license to use, reproduce, modify,
            distribute, publish, display, and create derivative works from such content for lawful business purposes.
          </p>
        </Section>

        <Section title="Disclaimers">
          <p>Our Site may contain links to third-party websites for your convenience.</p>
          <p>These links do not constitute an endorsement or sponsorship of those websites, their content, products, or services.</p>
          <p>
            All information, products, and services provided through this Site are offered "as is" without warranties of any kind, either express or
            implied.
          </p>
          <p>
            To the fullest extent permitted by law, Blanco Pro Services LLC disclaims all warranties, including implied warranties of merchantability
            and fitness for a particular purpose.
          </p>
          <p>
            You agree to indemnify and hold harmless Blanco Pro Services LLC, its owners, employees, contractors, affiliates, and agents from any
            claims, damages, liabilities, costs, or expenses arising from your use of the Site or your violation of these Terms & Conditions.
          </p>
        </Section>

        <Section title="Online Commerce">
          <p>
            Certain portions of this Site may allow you to purchase products or services offered either by Blanco Pro Services LLC or by third-party
            vendors.
          </p>
          <p>
            When purchasing from third parties, information such as payment details may be collected by both the merchant and us in accordance with
            each party's privacy practices.
          </p>
          <p>
            Blanco Pro Services LLC is not responsible for the quality, accuracy, delivery, reliability, or performance of products or services
            provided by third parties.
          </p>
          <p>Your dealings with third-party vendors are solely between you and those vendors.</p>
        </Section>

        <Section title="Registration & Passwords">
          <p>Certain features of the Site may require account registration.</p>
          <p>You agree to provide accurate, complete, and current registration information and to keep that information updated.</p>
          <p>You are responsible for maintaining the confidentiality of your username and password and for all activity that occurs under your account.</p>
          <p>
            If you suspect unauthorized use of your account, notify us immediately at{" "}
            <a className="text-gold hover:underline" href="mailto:sevice@blancopros.com">sevice@blancopros.com</a>.
          </p>
          <p>
            Blanco Pro Services LLC is not responsible for losses resulting from unauthorized use of your account due to your failure to safeguard your
            login credentials.
          </p>
        </Section>

        <Section title="Termination">
          <p>
            We reserve the right to suspend or terminate your access to the Site at any time, without prior notice, if we believe you have violated
            these Terms & Conditions or engaged in unlawful or inappropriate conduct.
          </p>
          <p>Upon termination, your right to use the Site immediately ends.</p>
        </Section>

        <Section title="Governing Law">
          <p>
            These Terms & Conditions shall be governed by and interpreted in accordance with the laws of the State of New Jersey, without regard to
            conflict of law principles.
          </p>
          <p>
            Any dispute arising from these Terms shall be resolved through binding arbitration or in a court of competent jurisdiction located in the
            State of New Jersey, as permitted by applicable law.
          </p>
        </Section>

        <Section title="Changes to These Terms & Conditions">
          <p>We may update these Terms & Conditions periodically.</p>
          <p>The most current version will always be available on our website and will include the effective date.</p>
          <p>Continued use of the Site after any updates constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section title="Contact Us">
          <p>If you have questions regarding these Terms & Conditions, please contact us:</p>
          <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-6 mt-4 space-y-2">
            <p className="font-display font-bold text-foreground">Blanco Pro Services LLC</p>
            <p>
              119 Lincoln Street
              <br />
              South Bound Brook, NJ 08880
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              <a className="text-gold hover:underline" href="tel:+19088956133">+1 (908) 895-6133</a>
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a className="text-gold hover:underline" href="mailto:sevice@blancopros.com">sevice@blancopros.com</a>
            </p>
            <p>
              <strong>Website:</strong>{" "}
              <a className="text-gold hover:underline" href="https://www.blancopros.com">https://www.blancopros.com</a>
            </p>
          </div>
          <p className="mt-6">
            By using our website and services, you acknowledge that you have read, understood, and agree to these Terms & Conditions.
          </p>
        </Section>
      </div>
    </div>
  );
}
